import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigationType, useParams } from "react-router";
import { useCollection, useCurrentUserID, useSpaceUserID } from "@/api/hooks";
import { useGetNoteRecordsQuery, useGetRecordsTimeRangeQuery } from "@/api/api";
import { IntervalsRange } from "@/engine/engine";
import { fitIntervalsToSeconds } from "./fitRange";
import { intervalsAreEqual } from "./intervals";
import { QuickIntervalTarget } from "./quickIntervals";
import { safeName } from "./recordTree";
import { HGInterval } from "./types";
import getGlobalStacks from "@/state/globalStacks";
import {
  getRecordsViewOptions,
  setRecordsViewOption,
} from "@/state/recordsView";

/**
 * The interval pair covering every record of one collection, or of the whole
 * space when `collectionID` is undefined. `target` is null while the range is
 * unknown — still loading, or nothing timed in scope; `hasData` tells those two
 * apart.
 *
 * The server widens the scope to the collection *and its descendants*
 * (`get_effective_collection_ids`), which is what makes this meaningful for a
 * collapsed row in the sidebar as well as for the collection page.
 */
export function useCollectionTimeRange(
  collectionID: string | undefined,
  options: { skip?: boolean } = {},
) {
  const userID = useCurrentUserID();
  // `currentData`, never `data`: through a switch from one collection to
  // another `data` still holds the collection just left, and a caller acting on
  // it fits the new page to the old page's range and then never hears the real
  // answer, having already acted.
  const { currentData: timeRange, isFetching } = useGetRecordsTimeRangeQuery(
    { userID, collectionID },
    { skip: options.skip },
  );

  const minSecond = timeRange?.min_second ?? null;
  const maxSecond = timeRange?.max_second ?? null;
  // Memoized because fitting runs the WASM engine once per level of the fit
  // chain, and because callers below want a dependency that only changes when
  // the range does.
  const target = useMemo(
    () =>
      minSecond != null && maxSecond != null
        ? fitIntervalsToSeconds(BigInt(minSecond), BigInt(maxSecond))
        : null,
    [minSecond, maxSecond],
  );

  const span = useMemo(
    () =>
      minSecond != null && maxSecond != null
        ? { minSecond: BigInt(minSecond), maxSecond: BigInt(maxSecond) }
        : null,
    [minSecond, maxSecond],
  );

  return {
    target,
    /** The records' own extent in seconds — `target` rounded out to ticks. */
    span,
    isFetching,
    hasRange: !!minSecond,
    /** The server has answered, so a null `target` means "nothing timed". */
    hasData: !options.skip && timeRange !== undefined,
  };
}

/**
 * The interval pair covering every record of the collection currently in the
 * URL (or of the whole space when there is none).
 *
 * Keyed on the *resolved* collection id, never the URL segment: that segment may
 * be a slug or an old slug, each of which would open its own cache entry for the
 * same collection. Skipped entirely until it resolves, so the wait never shows
 * up as the whole space's range.
 */
export function useAllRecordsTarget() {
  const { collectionID: collectionRef } = useParams();
  const collectionID = useCollection()?.id;
  const resolving = !!collectionRef && !collectionID;
  const { target, span, isFetching, hasData } = useCollectionTimeRange(
    collectionID,
    { skip: resolving },
  );

  return { target, span, isFetching: isFetching || resolving, hasData };
}

/**
 * Whether the range on screen already takes in every record, fitted or not — a
 * range wider than the records' span shows all of them just the same.
 */
export function rangeContainsAllRecords(
  span: { minSecond: bigint; maxSecond: bigint } | null,
  interval: HGInterval,
  endInterval: HGInterval | null,
): boolean {
  if (!span) return false;
  const [start, end] = IntervalsRange(interval, endInterval, "s");
  return start <= span.minSecond && span.maxSecond <= end;
}

/** Whether the range on screen is already the one covering every record. */
export function isFittedToAllRecords(
  target: QuickIntervalTarget | null,
  interval: HGInterval,
  endInterval: HGInterval | null,
): boolean {
  if (!target) return false;
  if (!intervalsAreEqual(target.interval, interval)) return false;
  return target.endInterval === null
    ? endInterval === null
    : intervalsAreEqual(target.endInterval, endInterval);
}

/**
 * Show everything the collection has: the whole span on the time axis, or the
 * notes tab when it has nothing on that axis at all. Records that are not timed
 * are invisible in every interval, so widening the range for a collection of
 * pure notes would land on an empty timeline.
 */
function applyShowAllRecords(target: QuickIntervalTarget | null) {
  if (!target) {
    setRecordsViewOption("mode", "notes");
    return;
  }
  setRecordsViewOption("mode", "time");
  getGlobalStacks().setIntervals(target.interval, target.endInterval);
}

/**
 * Whether the collection has any note — a record with no place on the time axis.
 *
 * The list itself rather than a count, because it is the very request the notes
 * tab makes when it opens: keyed exactly as `Records` keys it, the answer is
 * already in the cache by the time the press that asks for the tab arrives.
 * `selectFromResult` keeps the records out of the subscription, so the sidebar
 * re-renders when the answer flips, not whenever a note is edited.
 *
 * `currentData`, never `data`: through a switch from one collection to another
 * `data` still holds the collection just left, and a press acting on it would
 * open an empty notes tab on the strength of the previous collection's notes.
 */
function useCollectionHasNotes(collectionID: string | undefined) {
  const userID = useSpaceUserID();

  return useGetNoteRecordsQuery(
    { userID, collectionID },
    {
      skip: !userID || !collectionID,
      selectFromResult: ({ currentData }) => ({
        hasNotes: !!currentData && currentData.length > 0,
        /** The server has answered *for this collection*. */
        answered: currentData !== undefined,
      }),
    },
  );
}

/**
 * Pressing the collection that is already open. It widens the range to fit every
 * record; once the view is already showing all of them — the range fits, or the
 * collection has nothing timed to fit — it flips to the notes tab instead, and
 * back again from there.
 *
 * The flip only ever lands somewhere with records in it. A collection whose
 * records are all timed has nothing in its notes tab, one with nothing timed has
 * nothing on its timeline, and flipping to either would answer the press with an
 * empty view — so in that case the press does nothing at all.
 *
 * Null while there is nothing at all to do, so callers can leave the click as a
 * plain no-op.
 */
export function useShowAllRecords() {
  const { collectionID: collectionRef } = useParams();
  const { target, hasData } = useAllRecordsTarget();
  // The resolved id, not the URL segment: a slug and an old slug of the same
  // collection would each open their own cache entry.
  const { hasNotes } = useCollectionHasNotes(useCollection()?.id);
  // Notes live in a collection, so outside one there is no tab to flip to and
  // the press is only ever a fit — the same derivation Records makes.
  const hasNotesTab = !!collectionRef;

  return useMemo(() => {
    if (!target && !hasNotesTab) return null;

    return () => {
      const stacks = getGlobalStacks();
      // Read at click time rather than through the subscribing hooks: the
      // sidebar this hangs off must not re-render on every time navigation.
      const inNotes = hasNotesTab && getRecordsViewOptions().mode === "notes";
      if (inNotes) {
        // Nothing timed means there is no timeline to go back to: the flip
        // would land on an empty one, so the press stays in notes instead.
        if (!target) return;
        // Back to the timeline, fitted in the same press.
        setRecordsViewOption("mode", "time");
        stacks.setIntervals(target.interval, target.endInterval);
        return;
      }

      const fitted = isFittedToAllRecords(
        target,
        stacks.getInterval(),
        stacks.getEndInterval(),
      );
      // A range still in flight is not the same as no range: flipping to notes
      // on it would make a press during the first moments of a page do the
      // opposite of what the same press does a moment later.
      if (!target && !hasData) return;
      if (!target || fitted) {
        // Everything the collection has is already on screen. Only a notes tab
        // with something in it is worth flipping to; an empty one would read as
        // the press having emptied the view.
        if (hasNotesTab && hasNotes) setRecordsViewOption("mode", "notes");
        return;
      }
      stacks.setIntervals(target.interval, target.endInterval);
    };
  }, [target, hasData, hasNotes, hasNotesTab]);
}

/**
 * A collection whose records all sit in one tab opens that tab.
 *
 * The two tabs hold disjoint halves of a collection: the timeline holds the
 * records with a place on the time axis, the notes tab the ones without. A
 * collection that has only one of the two kinds therefore has an empty tab, and
 * arriving in it — because that is where the last collection was left, and the
 * mode is persisted — reads as an empty collection. So whichever tab actually
 * holds the records wins, whatever was open before.
 *
 * Only when the answer is unambiguous: with records in both tabs, or in
 * neither, the mode the user left behind stands.
 *
 * Unlike the boot fit below this runs on every collection opened, since it is
 * about which half of *this* collection is worth showing rather than about
 * where the time cursor happens to be. Decided once per collection, so the user
 * can still switch tabs by hand and stay there.
 */
export function useAutoSelectRecordsMode() {
  const { collectionID: collectionRef } = useParams();
  const collectionID = useCollection()?.id;
  const { target, hasData } = useAllRecordsTarget();
  const { hasNotes, answered } = useCollectionHasNotes(collectionID);
  const decided = useRef<string | null>(null);

  useEffect(() => {
    if (!collectionRef || !collectionID) {
      // Left for the space page: opening this collection again is a fresh
      // arrival and gets a fresh decision.
      decided.current = null;
      return;
    }
    if (decided.current === collectionID) return;
    // Both halves have to have reported before either can be called empty.
    if (!hasData || !answered) return;
    decided.current = collectionID;
    const hasTimed = !!target;
    if (hasTimed === hasNotes) return;
    setRecordsViewOption("mode", hasTimed ? "time" : "notes");
  }, [collectionRef, collectionID, hasData, answered, target, hasNotes]);
}

/**
 * A collection set to open on its own time range (`fit_range_on_open`) does so
 * whenever it is arrived at inside the running app: the range fits its records,
 * or the notes tab opens when it has none on the time axis — what a link opened
 * cold already does for every collection.
 *
 * Forward arrivals only. Back and forward land on a history entry that carries
 * its own stacks, and refitting would throw away the place being returned to;
 * that includes the boot page, whose navigation type is POP and which
 * `useShowAllRecordsOnBoot` already takes care of. Nor the dive into a title
 * record's collection from its row on the timeline, which arrives with
 * `keepRange` in the router state: that row was on screen in the range being
 * looked at, and going down into it is asking what sits inside it there.
 *
 * Decided once per collection, like the mode above: every move of the time
 * cursor is itself a PUSH onto the same URL, so without that this would snap
 * the range back after each one. Toggling the setting on while the collection
 * is open therefore takes effect from the next arrival, not on the spot.
 */
export function useFitRangeOnArrival() {
  const { collectionID: collectionRef } = useParams();
  const collection = useCollection();
  const collectionID = collection?.id;
  const fitOnOpen = !!collection?.fit_range_on_open && !collection.provider_key;
  const { target, hasData } = useAllRecordsTarget();
  const navigationType = useNavigationType();
  const keepRange = !!useLocation().state?.keepRange;
  const decided = useRef<string | null>(null);

  useEffect(() => {
    if (!collectionRef || !collectionID) {
      // Left for the space page: coming back is a fresh arrival.
      decided.current = null;
      return;
    }
    if (decided.current === collectionID) return;
    if (navigationType === "POP" || keepRange || !fitOnOpen) {
      decided.current = collectionID;
      return;
    }
    // Waiting for the answer, not for a range: a collection with nothing timed
    // answers with nulls and still has a tab to open.
    if (!hasData) return;
    decided.current = collectionID;

    const stacks = getGlobalStacks();
    if (
      isFittedToAllRecords(
        target,
        stacks.getInterval(),
        stacks.getEndInterval(),
      )
    )
      return;
    applyShowAllRecords(target);
  }, [
    collectionRef,
    collectionID,
    fitOnOpen,
    hasData,
    target,
    navigationType,
    keepRange,
  ]);
}

/** `/{user}/{collection}` — the one shape of URL this applies to. */
function isCollectionPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  // "-" is the reserved record segment, "record" the legacy permalink.
  return (
    segments.length === 2 && segments[0] !== "record" && segments[1] !== "-"
  );
}

/**
 * Armed once per page load, and only when the app booted straight onto a
 * collection page. Module-level rather than a ref because it is a property of
 * the page load, not of any component: the Records instance that consumes it
 * may not be the one mounted at boot.
 */
let pendingBootFit = isCollectionPath(window.location.pathname);

/**
 * A link opened cold lands on a collection showing all of its records: the range
 * fits its whole span, or the notes tab opens when it has nothing on the time
 * axis. Without this the page opens on wherever the time cursor happened to be —
 * "today" in a fresh tab — and a collection whose records sit in 1789 reads as
 * empty.
 *
 * Only the page the app *booted* on. Picking another collection inside a running
 * app deliberately keeps the interval, which is what makes browsing several
 * collections over one span possible at all; pressing the collection that is
 * already open is how you ask for its own span there (`useShowAllRecords`), and
 * a collection can opt into always opening on it (`useFitRangeOnArrival`).
 *
 * A reload is left alone too: it arrives with the stacks in its history entry
 * and in sessionStorage, and re-fitting would throw away the place the user was
 * reloading.
 */
export function useShowAllRecordsOnBoot() {
  const collectionID = useCollection()?.id;
  const { target, hasData } = useAllRecordsTarget();
  const navigationType = useNavigationType();
  const restored = !!useLocation().state?.stacks;

  useEffect(() => {
    if (!pendingBootFit) return;
    if (navigationType === "PUSH") {
      // Navigated on before the range came back; the boot page is over.
      pendingBootFit = false;
      return;
    }
    if (!collectionID) return;
    // Waiting for the answer, not for a range: a collection with nothing timed
    // answers with nulls and still has a tab to open.
    if (!hasData) return;

    // Spent even when skipped below, so a response arriving late cannot fit the
    // range a moment after the user has settled somewhere.
    pendingBootFit = false;
    if (navigationType === "POP" && restored) return;
    applyShowAllRecords(target);
  }, [collectionID, hasData, target, navigationType, restored]);
}

/**
 * "1789 - 1799", or a single name when the whole range fits one tick.
 *
 * The granularity is whatever `fitIntervalsToSeconds` settled on, so it adapts
 * on its own: years for a decade, centuries for an era. `safeName` rather than
 * `getName` because the interval type is not known statically and `getName`
 * throws for one it has no format for.
 */
export function formatFittedRange(target: QuickIntervalTarget): string {
  const start = safeName(target.interval);
  if (!target.endInterval) return start;
  return `${start} - ${safeName(target.endInterval)}`;
}
